/* import __COLOCATED_TEMPLATE__ from './conversation-part-action-type-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import type IntlService from 'embercom/services/intl';
import type { SafeString } from '@ember/template/-private/handlebars';
import { isEmpty } from '@ember/utils';
import { type SelectedFilter } from 'embercom/services/filter-pattern-service';
import { type FilterBarItem } from 'embercom/services/filter-pattern-service';

interface Args {
  initiallyOpened: boolean;
  icon: string;
  type: string;
  name: string;
  label: string;
  onClose: () => void;
  selected: SelectedFilter;
  setSelected: (filterValues: string[], operator?: string) => void;
  analyticsObject: string;
  removeFilter: () => void;
  isDisabled: boolean;
}

enum PartActionType {
  Reply = 'reply',
  Assignment = 'assignment',
  Close = 'close',
  Note = 'note',
  Snooze = 'snooze',
  Reopen = 'reopen',
  Any = REPORTING_FILTER_SELECT_ALL,
}

interface ActionType {
  [key: string]: SafeString | string;
}

const ANALYTIC_NAME = 'conversation_part_action_type';

export default class ConversationPartActionTypeFilter extends Component<Args> {
  @service intercomEventService: any;
  @service intl!: IntlService;
  @tracked label = this.currentLabel;
  @tracked selectedDropdownItems: string[] = this.args.selected.values || [];
  @tracked operator: string = this.args.selected.operator || 'category';

  get currentLabel() {
    if (
      isEmpty(this.selectedDropdownItems) ||
      this.selectedDropdownItems[0] === PartActionType.Any
    ) {
      return this.anyActionType[PartActionType.Any].toString();
    } else {
      let allActionTypes = { ...this.anyActionType, ...this.partActions };
      let translatedSelectedDropdownItems = this.selectedDropdownItems.map(
        (value) => allActionTypes[value],
      );
      return translatedSelectedDropdownItems.join(', ');
    }
  }

  private get anyActionType(): ActionType {
    return {
      [PartActionType.Any]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.conversation-part-action-type.any',
      ),
    };
  }

  private get partActions(): ActionType {
    return {
      [PartActionType.Reply]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.conversation-part-action-type.reply',
      ),
      [PartActionType.Assignment]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.conversation-part-action-type.assignment',
      ),
      [PartActionType.Close]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.conversation-part-action-type.close',
      ),
      [PartActionType.Note]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.conversation-part-action-type.note',
      ),
      [PartActionType.Snooze]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.conversation-part-action-type.snooze',
      ),
      [PartActionType.Reopen]: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.conversation-part-action-type.reopen',
      ),
    };
  }

  private mapToDropDownItems(partActions: ActionType): FilterBarItem[] {
    return Object.entries(partActions).map(([key, translationKey]) => ({
      text: translationKey.toString(),
      value: key,
      isSelected: this.selectedDropdownItems.includes(key),
    }));
  }

  get allItems() {
    return this.mapToDropDownItems(this.partActions);
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: ANALYTIC_NAME,
      object: this.args.analyticsObject,
      operator: this.operator,
    });
  }

  @action
  onClose() {
    if (this.isDestroying) {
      return;
    }

    this.label = this.currentLabel;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::ConversationPartActionTypeFilter': typeof ConversationPartActionTypeFilter;
    'reporting/custom/chart-builder/filter-bar/conversation-part-action-type-filter': typeof ConversationPartActionTypeFilter;
  }
}
